import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isLoginSecretStorageUnlockActive } from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import { VerificationErrorEnum } from 'contexts/MatrixDeviceVerificationContext/utils';
import useToast from 'contexts/ToastProvider/useToast';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import logger from 'utils/logging/faro';

import { withSecretStorageKeyCache } from './secretStorageCallbacks';
import { bootstrapCrossSigningWithAuth, unlockSecretStorageForLogin } from './unlockSecretStorageForLogin';

const useSecretStorageSecurityCode = () => {
  // During login unlock the client is only on `matrixClientRef` (context client is still null),
  // so resolve via the ref to let create/unlock run before the login gate resolves.
  const { matrixClient: contextClient, matrixClientRef } = useMatrixContext({ requireDefinedMatrixClient: false });
  const matrixClient = contextClient ?? (isLoginSecretStorageUnlockActive() ? matrixClientRef.current : null);
  const { refreshCurrentDeviceStatus, setKeyRestoreComplete, hasSecurityCode } = useMatrixDeviceContext();
  const { mxUserId, mxDeviceId } = useMxCookies();
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [errorKey, setErrorKey] = useState<VerificationErrorEnum | null>(null);
  const [recoveryKey, setRecoveryKey] = useState<string | undefined>();

  const clearError = useCallback(() => {
    setErrorKey(null);
  }, []);

  const createSecurityCode = useCallback(
    async (securityCode: string) => {
      setIsCreating(true);
      setErrorKey(null);

      try {
        if (!matrixClient) throw new Error('Matrix client not ready');

        await withSecretStorageKeyCache(async () => {
          const crypto = matrixClient.getCrypto();
          if (!crypto) throw new Error('Crypto not ready');

          const generatedKey = await crypto.createRecoveryKeyFromPassphrase(securityCode);
          setRecoveryKey(generatedKey.encodedPrivateKey);

          const backupInfo = await crypto.getKeyBackupInfo().catch(() => null);
          const isCrossSigningReady = await crypto.isCrossSigningReady();

          if (!isCrossSigningReady) {
            await bootstrapCrossSigningWithAuth(crypto);
          }

          await crypto.bootstrapSecretStorage({
            createSecretStorageKey: async () => generatedKey,
            setupNewSecretStorage: true,
            setupNewKeyBackup: !backupInfo,
          });
        });

        await refreshCurrentDeviceStatus();
      } catch (err) {
        logger.error('Failed to create security code', err);
        setErrorKey(VerificationErrorEnum.UNKNOWN_ERROR);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [matrixClient, refreshCurrentDeviceStatus]
  );

  const unlockWithSecurityCode = useCallback(
    async (securityCode: string) => {
      setIsUnlocking(true);
      setErrorKey(null);

      try {
        if (!matrixClient) throw new Error('Matrix client not ready');

        // hasSecurityCode===null means the check is still in progress, so we must not skip restore.
        await unlockSecretStorageForLogin(matrixClient, securityCode, {
          userId: mxUserId,
          deviceId: mxDeviceId,
          skipKeyRestoreWhenNoBackup: hasSecurityCode === false,
          onKeyRestoreComplete: () => setKeyRestoreComplete(true),
          onKeyRestoreError: () => pushToast({ variant: 'error', title: t('messenger.key_restore_failed') }),
        });

        await refreshCurrentDeviceStatus();
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : VerificationErrorEnum.UNKNOWN_ERROR;
        logger.error('Failed to unlock with security code', err);
        setErrorKey(
          errorMsg === VerificationErrorEnum.INVALID_SECURITY_CODE
            ? VerificationErrorEnum.INVALID_SECURITY_CODE
            : VerificationErrorEnum.UNKNOWN_ERROR
        );
        throw err;
      } finally {
        setIsUnlocking(false);
      }
    },
    [
      matrixClient,
      mxUserId,
      mxDeviceId,
      refreshCurrentDeviceStatus,
      setKeyRestoreComplete,
      hasSecurityCode,
      pushToast,
      t,
    ]
  );

  return {
    createSecurityCode,
    unlockWithSecurityCode,
    isCreating,
    isUnlocking,
    errorKey,
    recoveryKey,
    clearError,
  };
};

export default useSecretStorageSecurityCode;
